import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { format, formatDistanceToNow } from 'date-fns'
import { de } from 'date-fns/locale'
import { mutation, query } from '../api'

import Loader from '../components/Loader'
import Table from '../components/Table'
import { classNames } from '../helper'
import { useNotificationContext } from '../context/notification-context'
import {
  FUNNEL_FORM_SOURCE,
  filterByDays,
  filterByFollowup,
  getFunnelDropouts,
  withFollowup
} from '../helper/funnelDropouts'

const PERIODS = [
  { label: '7 Tage', days: 7 },
  { label: '30 Tage', days: 30 },
  { label: '90 Tage', days: 90 },
  { label: 'Alle', days: null }
]

const FOLLOWUP_FILTERS = [
  { label: 'Offen', value: 'open' },
  { label: 'Kontaktiert', value: 'contacted' },
  { label: 'Alle', value: 'all' }
]

const FilterButtons = ({ options, value, onChange }) => (
  <div className='flex gap-1.5'>
    {options.map((option) => (
      <button
        key={option.label}
        onClick={() => onChange(option.value)}
        className={classNames(
          'rounded-md px-2.5 py-1 text-xs font-medium',
          value === option.value
            ? 'bg-indigo-600 text-white'
            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
)

// Schreibt die Markierung nach Form.data.followup. data wird direkt vorher
// frisch geladen, weil updateForm das ganze Feld ersetzt. Die Backend-Handler
// auf der Form-Tabelle reagieren nur auf Status-/Lifecycle-Wechsel, die
// Markierung löst also nichts aus.
const FollowupCell = ({ row }) => {
  const [isSaving, setIsSaving] = useState(false)
  const queryClient = useQueryClient()
  const { error } = useNotificationContext()
  const { user } = useAuthenticator((context) => [context.user])
  const { contacted_at: contactedAt, contacted_by: contactedBy } = row.original

  const save = async (formId, followup) => {
    setIsSaving(true)
    try {
      const form = await query({ query: 'getForm', variables: { id: formId } })
      await mutation({
        mutation: 'updateForm',
        input: {
          id: formId,
          data: JSON.stringify(withFollowup(form?.data, followup))
        }
      })
      await queryClient.invalidateQueries(['funnel-forms'])
    } catch (_) {
      error('Markierung konnte nicht gespeichert werden')
    } finally {
      setIsSaving(false)
    }
  }

  if (contactedAt) {
    return (
      <span>
        <span title={contactedBy || undefined}>
          Kontaktiert am {format(new Date(contactedAt), 'dd.MM.yyyy')}
        </span>
        <button
          disabled={isSaving}
          onClick={() => save(row.original.followup_form_id, null)}
          className='ml-2 text-xs text-gray-400 hover:text-gray-700 disabled:opacity-50'
        >
          zurücksetzen
        </button>
      </span>
    )
  }

  return (
    <button
      disabled={isSaving}
      onClick={() =>
        save(row.original.id, {
          contacted_at: new Date().toISOString(),
          contacted_by: user?.attributes?.email || user?.username || null
        })
      }
      className='rounded-md bg-white px-2.5 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50'
    >
      {isSaving ? 'Speichert ...' : 'Als kontaktiert markieren'}
    </button>
  )
}

const columns = [
  {
    header: 'NACHFASSEN',
    accessorKey: 'contacted_at',
    cell: FollowupCell
  },
  {
    header: 'LETZTE AKTIVITÄT',
    accessorKey: 'activity_time',
    cell: (props) => (
      <span title={format(new Date(props.getValue()), 'dd.MM.yyyy HH:mm')}>
        {formatDistanceToNow(new Date(props.getValue()), {
          addSuffix: true,
          locale: de
        })}
      </span>
    )
  },
  {
    header: 'E-MAIL-ADRESSE',
    accessorKey: 'email',
    cell: (props) => (
      <a
        href={`mailto:${props.getValue()}`}
        className='text-indigo-600 hover:text-indigo-900'
      >
        {props.getValue()}
      </a>
    )
  },
  {
    header: 'FIRMA',
    accessorKey: 'company',
    cell: ({ row }) => (
      <span>
        {row.original.company || '–'}
        {row.original.employees && (
          <span className='ml-1 text-gray-400'>({row.original.employees})</span>
        )}
      </span>
    )
  },
  {
    header: 'ABBRUCH BEI',
    accessorKey: 'activity_label'
  },
  {
    header: 'FORTSCHRITT',
    accessorKey: 'lead_progress',
    cell: (props) =>
      props.getValue() === null ? '–' : `${Math.round(props.getValue())} %`
  },
  {
    header: 'VERSUCHE',
    accessorKey: 'attempts'
  },
  {
    header: 'SPRACHE',
    accessorKey: 'language',
    cell: (props) => props.getValue() || '–'
  },
  {
    header: 'QUELLE',
    accessorKey: 'source'
  },
  {
    header: 'REF-LINK',
    accessorKey: 'ref_link',
    cell: (props) => props.getValue() || '–'
  }
]

const FunnelDropouts = () => {
  const [days, setDays] = useState(30)
  const [followup, setFollowup] = useState('open')

  const { data: forms, isLoading } = useQuery({
    queryKey: ['funnel-forms'],
    queryFn: () =>
      query({
        query: 'listForms',
        variables: {
          filter: { form_source: { eq: FUNNEL_FORM_SOURCE } }
        }
      })
  })

  const dropouts = useMemo(() => getFunnelDropouts(forms || []), [forms])
  const visible = useMemo(
    () => filterByFollowup(filterByDays(dropouts, days), followup),
    [dropouts, days, followup]
  )

  if (isLoading) {
    return <Loader loadingText={'Funnel-Abbrecher werden geladen ...'} />
  }

  return (
    <div>
      <div className='sm:flex sm:items-center'>
        <div className='sm:flex-auto'>
          <h1 className='text-base font-semibold leading-6 text-gray-900'>
            Funnel-Abbrecher
          </h1>
          <p className='mt-2 text-sm text-gray-700'>
            Leads, die den Funnel mit einer Business-Mail begonnen, aber nicht
            abgeschlossen haben. Wer den Funnel später mit derselben Mail
            abschließt, fällt raus. Mehrere Versuche derselben Mail werden
            zusammengefasst.
          </p>
        </div>
      </div>
      <div className='mt-6 flex flex-wrap items-center gap-3'>
        <FilterButtons
          options={PERIODS.map((p) => ({ label: p.label, value: p.days }))}
          value={days}
          onChange={setDays}
        />
        <FilterButtons
          options={FOLLOWUP_FILTERS}
          value={followup}
          onChange={setFollowup}
        />
        <p className='text-sm text-gray-700'>
          <span className='font-semibold'>{visible.length}</span> Abbrecher
        </p>
      </div>
      <div className='mt-4 flow-root'>
        <div className='-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
          <div className='inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8'>
            <Table data={visible} columns={columns} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default FunnelDropouts
